import useIsReadOnlyMode from 'components/v2/compiler/hooks/useIsReadOnlyMode';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { ProcessStatus } from 'constants/constants';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { PCRModel } from 'services/EpdClient';
import FileService from 'services/FileService';
import PcrService from 'services/PcrService';
import { Option } from 'types/types';
import PcrSelectorDialog from './PcrSelectorDialog';
import PcrSelectorFilesDialog from './PcrSelectorFilesDialog';
import { TooltipPosition } from 'components/controls/Tooltip';
import SelectorButton from 'components/v2/buttons/SelectorButton';

interface PcrProps {
  label?: string | undefined;
  tooltip?: string | undefined;
  tooltipPosition?: TooltipPosition;
  pcr: PCRModel | null;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  onChangePcr: (pcr: PCRModel | null) => void;
  error: any;
  disclaimerText?: string | null;
  onlyConstructionPcrs?: boolean;
}

const PcrSelector: React.FunctionComponent<PcrProps> = ({
  label,
  tooltip,
  tooltipPosition,
  pcr,
  disabled,
  required,
  onChangePcr,
  error,
  disclaimerText,
  onlyConstructionPcrs = false,
}) => {
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [pcrList, setPcrList] = useState<PCRModel[]>([]);
  const [productCategory, setProductCategory] = useState<Option | undefined>();
  const [productCategoryList, setProductCategoryList] = useState<Option[]>([]);
  const [showFiles, setShowFiles] = useState<boolean>(false);
  const [isEditOpened, setIsEditOpened] = useState<boolean>(false);
  const isReadOnly = useIsReadOnlyMode() || disabled;

  const findPcr = async (val: string, catId?: string, isDisabled?: boolean) => {
    const categoryId = onlyConstructionPcrs ? undefined : catId;

    setStatus(ProcessStatus.Fetching);
    const res = await PcrService.findPcr({
      smartFilter: val || '',
      productCategoryId: categoryId,
      isDisabled: isDisabled,
      targetEPDFormat: onlyConstructionPcrs ? 'compiler' : undefined,
      pcrType: 'PCR',
    });
    setPcrList(res);
    setStatus(ProcessStatus.Success);
  };

  const debounceLoadData = useCallback(_.debounce(findPcr, 500), []);

  React.useEffect(() => {
    const fetchPcr = async () => {
      if (!onlyConstructionPcrs && !productCategory) {
        setPcrList([]);
        return;
      }

      debounceLoadData('', productCategory?.value, false);
    };
    fetchPcr();
  }, [productCategory, debounceLoadData]);

  React.useEffect(() => {
    const fetchCats = async () => {
      const res = await PcrService.getAllCategories();
      setProductCategoryList(
        res.map((item) => {
          return { value: item.id!, label: item.title! };
        })
      );
    };

    fetchCats();
  }, []);

  const handleCloseDialog = () => {
    setIsEditOpened(false);
    if (!onlyConstructionPcrs) {
      setStatus(ProcessStatus.None);
      setPcrList([]);
      setProductCategory(undefined);
    }
  };

  const handleDownloadSelectedPcrFiles = (pcr: PCRModel | undefined) => {
    if (pcr?.documents && pcr.documents?.length > 1) {
      setShowFiles(true);
    } else if (pcr?.documents && pcr.documents?.length === 1) {
      window.open(FileService.getImageUrl(pcr.documents[0].id || ''), '_blank', 'noreferrer');
    }
  };

  const handleRemovePcr = () => {
    onChangePcr(null);
  };

  return (
    <>
      {label && (
        <SmartLabel
          label={label}
          required={required}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          hasValue={!!pcr}
          error={error}
        />
      )}
      <SelectorButton
        name="pcr"
        value={pcr ? pcr.fullName : ''}
        placeholder="Add product category rule"
        addButtonText="+ Add PCR"
        addButtonDisabled={!!pcr}
        onOpenSelector={() => setIsEditOpened(true)}
        showDeleteButton={!!pcr && !isReadOnly}
        onDeleteClicked={handleRemovePcr}
        showDownloadButton={!!pcr}
        onDownloadClicked={() => handleDownloadSelectedPcrFiles(pcr!)}
        downloadButtonDisabled={pcr?.documents && pcr.documents.length === 0}
        disabled={isReadOnly}
      />

      <PcrSelectorDialog
        visible={isEditOpened}
        onHide={handleCloseDialog}
        initialPcr={pcr}
        onPcrChange={onChangePcr}
        productCategoryList={productCategoryList}
        productCategory={productCategory}
        pcrList={pcrList}
        onProductCategorySelect={setProductCategory}
        status={status}
        disclaimerText={disclaimerText}
        disableCategorySelector={onlyConstructionPcrs}
      />

      <PcrSelectorFilesDialog
        pcrType="main"
        isOpened={showFiles}
        onHide={() => {
          setShowFiles(false);
        }}
        files={pcr?.documents ?? []}
      />
    </>
  );
};

export default PcrSelector;
